import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import { useToken } from '../../../../hooks/useToken';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  CircularProgress,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getTemplatesCatalog } from '../../../../actions/campaigns/get-templates.actions';
import { isCarouselWithParams } from '../../../../utils/campaign-template-filter.utils';
import { TemplateCard } from '..';
import { TEMPLATE_CATEGORY } from '../../../apps/campaigns/constants';
import TemplateCreator from '../../../../views/templates/TemplateForm/TemplateForm';

export const TemplatesModal = ({ open, onClose, onConfirm, initialSelectedId }) => {
  const [view, setView] = useState('list');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState(null);

  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const limit = 20;
  const searchTimerRef = useRef(null);
  const containerRef = useRef(null);

  const { id_customer } = useSelector((state) => state.agent);
  const { instance } = useMsal();
  const { tokenRequest } = useToken();


  useEffect(() => {
    if (open) {
      setView('list');
      resetAndLoad();
    } else {
      setTemplates([]);
      setSearchTerm('');
      setCategory(null);
      setSelectedTemplate(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);


  useEffect(() => {
    if (!open || view !== 'list') return;

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      resetAndLoad();
    }, 500);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);


  useEffect(() => {
    if (open && view === 'list') {
      resetAndLoad();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);


  const resetAndLoad = () => {
    setTemplates([]);
    setOffset(0);
    setHasMore(true);
    loadTemplates(0);
  };

  const loadTemplates = useCallback(
    async (currentOffset) => {
      if (currentOffset === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const { accessToken } = await instance.acquireTokenSilent(tokenRequest);

        const filters = {};
        if (searchTerm.trim()) {
          filters.name = searchTerm.trim();
        }
        if (category) {
          filters.category = category;
        }

        const result = await getTemplatesCatalog({
          idCustomer: id_customer,
          token: accessToken,
          offset: currentOffset,
          limit,
          ...filters,
        });

        const newTemplates = (result.items || []).filter((t) => !isCarouselWithParams(t));

        setTemplates((prev) =>
          currentOffset === 0 ? newTemplates : [...prev, ...newTemplates]
        );
        setOffset(currentOffset + newTemplates.length);
        setHasMore(newTemplates.length === limit);

        if (currentOffset === 0 && initialSelectedId && newTemplates.length > 0) {
          const initial = newTemplates.find((t) => t._id === initialSelectedId);
          if (initial) {
            setSelectedTemplate(initial);
          }
        }
      } catch (error) {
        console.error('Error al cargar templates:', error);
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [id_customer, instance, tokenRequest, searchTerm, category, initialSelectedId, limit]
  );

  const handleScroll = useCallback(() => {
    if (!containerRef.current || !hasMore || loadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    if (scrollPercentage > 0.8) {
      loadTemplates(offset);
    }
  }, [hasMore, loadingMore, offset, loadTemplates]);


  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryChange = (event, newCategory) => {
    setCategory(newCategory);
  };


  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };


  const handleConfirm = () => {
    if (selectedTemplate) {
      onConfirm(selectedTemplate);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleTemplateCreated = () => {
    setView('list');
    resetAndLoad();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={view === 'create' ? 'xl' : 'xl'}
      fullWidth
      PaperProps={{
        sx: {
          height: view === 'create' ? '95vh' : '85vh',
          maxHeight: view === 'create' ? '1000px' : '850px',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {view === 'list' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <TextField
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={handleSearchChange}
                size="small"
                sx={{ maxWidth: 360, width: "100%" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <ToggleButtonGroup
                value={category}
                exclusive
                onChange={handleCategoryChange}
                size="small"
                sx={{ flexShrink: 0, borderRadius: 1 }}
              >
                <ToggleButton value={TEMPLATE_CATEGORY.UTILITY}>Utilidad</ToggleButton>
                <ToggleButton value={TEMPLATE_CATEGORY.MARKETING}>Marketing</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                Seleccionar Template
              </Typography>
              <IconButton
                aria-label="cerrar"
                onClick={handleClose}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => setView('list')}
                sx={{ mr: 0.5 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Crear nuevo template
              </Typography>
            </Box>
            <IconButton
              aria-label="cerrar"
              onClick={handleClose}
              size="small"
              sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </DialogTitle>

      {view === 'list' ? (
        <>
          <Box
            ref={containerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 3,
              pb: 2,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 300,
                }}
              >
                <CircularProgress />
              </Box>
            ) : templates.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 300,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {searchTerm || category
                    ? 'No se encontraron templates con los filtros aplicados'
                    : 'No hay templates disponibles'}
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {templates.length} resultado{templates.length !== 1 ? 's' : ''}
                  </Typography>
                  {selectedTemplate && (
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
                      Seleccionado: {selectedTemplate.name}
                    </Typography>
                  )}
                </Box>
                <Grid container spacing={1.5}>
                  {templates.map((template) => (
                    <Grid item xs={12} sm={6} md={3} key={template._id}>
                      <TemplateCard
                        template={template}
                        selected={selectedTemplate?._id === template._id}
                        onSelect={handleSelectTemplate}
                      />
                    </Grid>
                  ))}
                </Grid>

                {loadingMore && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mt: 2,
                    }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                )}
              </>
            )}
          </Box>

          <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', borderTop: 1, borderColor: 'divider' }}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setView('create')}
              variant="outlined"
              size="small"
            >
              Crear nuevo template
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleClose} variant="outlined">
                Cancelar
              </Button>
              <Button
                onClick={handleConfirm}
                variant="contained"
                disabled={!selectedTemplate}
              >
                Seleccionar Template
              </Button>
            </Box>
          </DialogActions>
        </>
      ) : (
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 3,
            py: 2,
          }}
        >
          <TemplateCreator onSuccess={handleTemplateCreated} />
        </Box>
      )}
    </Dialog>
  );
};

TemplatesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  initialSelectedId: PropTypes.string,
};
